// 배포 시 안전한 업데이트를 위한 유틸리티
import { logger } from './logger';

interface UpdateInfo {
  version: string;
  timestamp: number;
  forceUpdate?: boolean;
}

// 버전 정보를 로컬 스토리지에 저장
export const saveVersionInfo = (version: string) => {
  try {
    const versionInfo: UpdateInfo = {
      version,
      timestamp: Date.now()
    };
    localStorage.setItem('zorder-version', JSON.stringify(versionInfo));
    logger.log('Version info saved:', versionInfo);
  } catch (error) {
    logger.error('Failed to save version info:', error);
  }
};

// 버전 정보를 로컬 스토리지에서 가져오기
export const getVersionInfo = (): UpdateInfo | null => {
  try {
    const versionInfo = localStorage.getItem('zorder-version');
    return versionInfo ? JSON.parse(versionInfo) : null;
  } catch (error) {
    logger.error('Failed to get version info:', error);
    return null;
  }
};

// 새 버전이 있는지 확인
export const checkForUpdate = (currentVersion: string): boolean => {
  const savedVersion = getVersionInfo();
  if (!savedVersion) {
    // 첫 방문이므로 버전 정보 저장
    saveVersionInfo(currentVersion);
    return false;
  }

  if (savedVersion.version !== currentVersion) {
    logger.log('New version detected:', {
      old: savedVersion.version,
      new: currentVersion
    });
    return true;
  }

  return false;
};

// 안전한 페이지 리로드
export const safeReload = () => {
  try {
    // 현재 상태 저장
    const currentState = {
      scrollPosition: window.scrollY,
      timestamp: Date.now()
    };
    sessionStorage.setItem('zorder-reload-state', JSON.stringify(currentState));
    
    // 페이지 리로드
    window.location.reload();
  } catch (error) {
    logger.error('Failed to safe reload:', error);
    // 폴백: 일반 리로드
    window.location.reload();
  }
};

// 리로드 후 상태 복원
export const restoreReloadState = () => {
  try {
    const savedState = sessionStorage.getItem('zorder-reload-state');
    if (savedState) {
      const state = JSON.parse(savedState);
      const timeDiff = Date.now() - state.timestamp;
      
      // 5초 이내의 리로드인 경우에만 상태 복원
      if (timeDiff < 5000 && state.scrollPosition) {
        window.scrollTo(0, state.scrollPosition);
      }
      
      // 상태 정보 삭제
      sessionStorage.removeItem('zorder-reload-state');
    }
  } catch (error) {
    logger.error('Failed to restore reload state:', error);
  }
};

// 캐시 무효화 헤더 추가
export const addCacheBustingHeaders = () => {
  // API 요청에 캐시 무효화 헤더 추가
  const originalFetch = window.fetch;
  window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
    const url = typeof input === 'string' ? input : input.toString();
    
    // API 요청에만 캐시 무효화 헤더 추가
    if (url.includes('/api/') || url.includes('sse/')) {
      const headers = new Headers(init?.headers);
      headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
      headers.set('Pragma', 'no-cache');
      headers.set('Expires', '0');
      
      return originalFetch(input, {
        ...init,
        headers
      });
    }
    
    return originalFetch(input, init);
  };
};

// 서비스 워커 업데이트 감지
export const detectServiceWorkerUpdate = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      // 업데이트 확인
      registration.update().then(() => {
        if (registration.waiting) {
          logger.log('Service Worker update available');
          
          // 사용자에게 업데이트 알림 (선택적)
          if (confirm('새로운 버전이 있습니다. 지금 업데이트하시겠습니까?')) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            window.location.reload();
          }
        }
      });
    });
  }
};

// 배포 시 안전한 초기화
export const safeDeploymentInit = (version: string) => {
  logger.log('Safe deployment initialization:', version);
  
  // 버전 체크
  const hasUpdate = checkForUpdate(version);
  
  // 캐시 무효화 헤더 추가
  addCacheBustingHeaders();
  
  // 서비스 워커 업데이트 감지
  detectServiceWorkerUpdate();
  
  // 리로드 상태 복원
  restoreReloadState();
  
  if (hasUpdate) {
    logger.log('Update detected, performing safe update...');
    // 새 버전 정보 저장
    saveVersionInfo(version);
  }
  
  return hasUpdate;
};

// 강제 업데이트 (긴급 배포 시)
export const forceUpdate = () => {
  logger.log('Force update triggered');
  
  // 모든 캐시 삭제
  if ('caches' in window) {
    caches.keys().then((cacheNames) => {
      return Promise.all(
        cacheNames.map((name) => {
          logger.log('Deleting cache:', name);
          return caches.delete(name);
        })
      );
    });
  }
  
  // 서비스 워커 재등록
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister();
      });
    });
  }
  
  // 페이지 리로드
  window.location.reload();
}; 